import {testIdProps} from '@github-ui/test-id-props'
import {CounterLabel, Label} from '@primer/react'
import {clsx} from 'clsx'

import type {FieldAggregate} from '../../hooks/use-aggregation-settings'
import styles from './aggregate-labels.module.css'

interface AggregateLabelsProps {
  aggregates: Array<FieldAggregate>
  hideItemsCount: boolean
  itemsCount: number
  columnLimit?: number
  counterClassName?: string
}

export function getAggregateDisplayValue(aggregate: FieldAggregate): string {
  if (Number.isInteger(aggregate.sum)) return aggregate.sum.toString()
  const rounded = aggregate.sum.toFixed(aggregate.maxDecimalPlaces)
  // Remove trailing zeroes (e.g., 123.190 -> 123.19) after the decimal point
  return rounded.replace(/\.?0+$/, '')
}

export const AggregateLabels = ({
  aggregates,
  hideItemsCount,
  itemsCount,
  columnLimit,
  counterClassName,
}: AggregateLabelsProps) => {
  const {itemCountContent, className} = getColumnLimitAndCountState({columnLimit, itemsCount})
  return (
    <>
      {!hideItemsCount && (
        <CounterLabel className={clsx(counterClassName, className)} {...testIdProps('column-items-counter')}>
          {itemCountContent}
        </CounterLabel>
      )}
      {aggregates.map(aggregate => (
        <Label
          key={`aggregate-sum-${aggregate.name}`}
          {...testIdProps(`column-sum-${aggregate.name}`)}
          className={clsx(counterClassName, styles.Label)}
        >
          {aggregate.name}: {getAggregateDisplayValue(aggregate)}
        </Label>
      ))}
    </>
  )
}

function getColumnLimitAndCountState({
  columnLimit,
  itemsCount,
}: {
  columnLimit: number | undefined
  itemsCount: number
}): {
  itemCountContent: string | number
  className: string
} {
  if (typeof columnLimit === 'undefined') {
    return {
      itemCountContent: itemsCount,
      className: styles.default,
    }
  }

  return {
    itemCountContent: `${itemsCount} / ${columnLimit}`,
    className: columnLimit < itemsCount ? styles.danger : styles.default,
  }
}

try{ AggregateLabels.displayName ||= 'AggregateLabels' } catch {}